import { Bucket } from "../../sdk/types/data";
import { TradeResult } from "../../sdk/types/data";

interface OrderPlacementTabProps {
  buckets: Bucket[];
  orderPlacementMnemonic: string;
  setOrderPlacementMnemonic: (value: string) => void;
  orderPlacementBucketId: string;
  setOrderPlacementBucketId: (value: string) => void;
  orderPlacementSide: "buy" | "sell";
  setOrderPlacementSide: (value: "buy" | "sell") => void;
  orderPlacementOrderType: "market" | "limit";
  setOrderPlacementOrderType: (value: "market" | "limit") => void;
  orderPlacementPrice: number;
  setOrderPlacementPrice: (value: number) => void;
  orderPlacementQuantity: number;
  setOrderPlacementQuantity: (value: number) => void;
  orderPlacementLeverage: number;
  setOrderPlacementLeverage: (value: number) => void;
  isReady: boolean;
  loading: boolean;
  result: TradeResult | null;
  handlePlaceOrder: () => Promise<void>;
  handleRandomize: () => void;
}

export default function OrderPlacementTab({
  buckets,
  orderPlacementMnemonic,
  setOrderPlacementMnemonic,
  orderPlacementBucketId,
  setOrderPlacementBucketId,
  orderPlacementSide,
  setOrderPlacementSide,
  orderPlacementOrderType,
  setOrderPlacementOrderType,
  orderPlacementPrice,
  setOrderPlacementPrice,
  orderPlacementQuantity,
  setOrderPlacementQuantity,
  orderPlacementLeverage,
  setOrderPlacementLeverage,
  isReady,
  loading,
  result,
  handlePlaceOrder,
  handleRandomize,
}: OrderPlacementTabProps) {
  return (
    <div style={{ marginTop: "20px" }}>
      <div className="hooks-test-form-grid">
        <div>
          <label className="hooks-test-label">
            Mnemonic:
            <input
              type="text"
              className="hooks-test-input"
              value={orderPlacementMnemonic}
              onChange={(e) => setOrderPlacementMnemonic(e.target.value)}
              placeholder="Enter mnemonic"
            />
          </label>
        </div>
        <div>
          <label className="hooks-test-label">
            Bucket ID:
            <select
              className="hooks-test-select"
              value={orderPlacementBucketId}
              onChange={(e) => setOrderPlacementBucketId(e.target.value)}
            >
              <option value="">Select a bucket...</option>
              {buckets.map((bucket) => (
                <option key={bucket.bucketId} value={bucket.bucketId}>
                  {bucket.bucketId} ({bucket.collateralAsset})
                </option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <label className="hooks-test-label">
            Side:
            <select
              className="hooks-test-select"
              value={orderPlacementSide}
              onChange={(e) =>
                setOrderPlacementSide(e.target.value as "buy" | "sell")
              }
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </label>
        </div>
        <div>
          <label className="hooks-test-label">
            Order Type:
            <select
              className="hooks-test-select"
              value={orderPlacementOrderType}
              onChange={(e) =>
                setOrderPlacementOrderType(
                  e.target.value as "market" | "limit",
                )
              }
            >
              <option value="market">Market</option>
              <option value="limit">Limit</option>
            </select>
          </label>
        </div>
        <div>
          <label className="hooks-test-label">
            Price:
            <input
              type="number"
              className="hooks-test-input"
              value={orderPlacementPrice || ""}
              onChange={(e) =>
                setOrderPlacementPrice(parseFloat(e.target.value) || 0)
              }
              placeholder="Enter price"
              step="0.01"
              disabled={orderPlacementOrderType === "market"}
            />
          </label>
        </div>
        <div>
          <label className="hooks-test-label">
            Quantity:
            <input
              type="number"
              className="hooks-test-input"
              value={orderPlacementQuantity || ""}
              onChange={(e) =>
                setOrderPlacementQuantity(parseFloat(e.target.value) || 0)
              }
              placeholder="Enter quantity"
              step="0.001"
            />
          </label>
        </div>
        <div>
          <label className="hooks-test-label">
            Leverage:
            <input
              type="number"
              className="hooks-test-input"
              value={orderPlacementLeverage || ""}
              onChange={(e) =>
                setOrderPlacementLeverage(parseFloat(e.target.value) || 1)
              }
              placeholder="Enter leverage"
              min="1"
              step="0.1"
            />
          </label>
        </div>
      </div>

      {/* Big Place Order Button */}
      <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
        <button
          className="hooks-test-button"
          onClick={handlePlaceOrder}
          disabled={
            !isReady ||
            loading ||
            !orderPlacementBucketId ||
            orderPlacementQuantity <= 0
          }
          style={{
            flex: 1,
            padding: "20px",
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          {loading
            ? "Placing Order..."
            : orderPlacementSide === "buy"
              ? "Place Buy Order"
              : "Place Sell Order"}
        </button>
        <button
          className="hooks-test-button"
          onClick={handleRandomize}
          disabled={buckets.length === 0}
          style={{
            padding: "20px",
            fontSize: "18px",
            fontWeight: "bold",
            minWidth: "120px",
          }}
        >
          Random
        </button>
      </div>

      {/* Result Display */}
      {result && (
        <div
          className="hooks-test-error-box"
          style={{
            marginTop: "15px",
            backgroundColor: result.success ? "#d4edda" : "#f8d7da",
            color: result.success ? "#155724" : "#721c24",
          }}
        >
          {result.success ? (
            <div>
              <strong>Success!</strong>
              {result.message && <div>{result.message}</div>}
              {result.orderId && <div>Order ID: {result.orderId}</div>}
            </div>
          ) : (
            <div>
              <strong>Error:</strong> {result.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
